import { useQuery } from '@tanstack/react-query'
import { Folder, FileAttachment01, Trash01 } from '#app/components/icons/solid'
import { useState, useMemo, useCallback, memo } from 'react'
import {
	Text,
	type Selection,
	ListLayout,
	Virtualizer,
} from 'react-aria-components'
import { FileUpload } from '#app/components/application/file-upload/file-upload-base.tsx'
import { Button } from '#app/components/base/buttons/button.tsx'
import { Input } from '#app/components/base/input/input.tsx'
import { Tree, TreeItem, TreeContent } from '#app/components/base/tree/tree.tsx'
import {
	useCreateUploaded,
	useUpdateUploaded,
	useDeleteUploaded,
	getUploadErrorMessage,
	transformIconsForUpload,
} from '#app/hooks/use-upload-mutations.ts'
import {
	processFiles,
	type ProcessedIcon,
	createTreeStructure,
	countSelectedIcons,
	getSelectedIcons,
	normalizeIconPaths,
} from '#app/lib/file-processor.ts'
import { trpc } from '#app/lib/trpc-tanstack.tsx'
import { cx } from '#app/utils/cx.ts'
import { useWorkspaceId } from '#app/utils/user.js'
import { LoadingIndicator } from '../loading-indicator/loading-indicator'

// Helper to format icon count
function formatCount(count: number): string {
	if (count >= 1000) {
		return `${(count / 1000).toFixed(1)}k`
	}
	return count.toString()
}

// Helper function to get all folder keys from tree structure
function getAllFolderKeys(
	nodes: ReturnType<typeof createTreeStructure>,
): string[] {
	return nodes.reduce<string[]>((acc, node) => {
		if (!node.isIcon && node.childItems.length > 0) {
			acc.push(node.id)
			acc.push(...getAllFolderKeys(node.childItems))
		}
		return acc
	}, [])
}

interface IconSetTreeProps {
	tree: ReturnType<typeof createTreeStructure>
	selectionState: {
		isSelectionMode: boolean
		selectedKeys: Set<string>
	}
	onSelectionChange: (keys: Selection) => void
	isProcessing: boolean
	isSaving: boolean
}

const IconSetTree = memo(function IconSetTree({
	tree,
	selectionState,
	onSelectionChange,
	isProcessing,
	isSaving,
}: IconSetTreeProps) {
	const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
		() => new Set(getAllFolderKeys(tree)),
	)

	const handleExpandedChange = useCallback(
		(keys: Set<React.Key>) => {
			if (isProcessing || isSaving) return
			const stringKeys = new Set<string>()
			for (const key of keys) {
				stringKeys.add(String(key))
			}
			setExpandedKeys(stringKeys)
		},
		[isProcessing, isSaving],
	)

	return (
		<Virtualizer
			layout={ListLayout}
			layoutOptions={{
				rowHeight: 36,
				estimatedRowHeight: 36,
			}}
		>
			<Tree
				className={cx(
					'max-h-64 min-h-0 overflow-y-auto transition-opacity duration-200',
					(isProcessing || isSaving) && 'opacity-50',
				)}
				aria-label="Icon set icons"
				selectionMode={selectionState.isSelectionMode ? 'multiple' : 'none'}
				selectedKeys={selectionState.selectedKeys}
				onSelectionChange={onSelectionChange}
				expandedKeys={expandedKeys}
				onExpandedChange={handleExpandedChange}
				items={tree}
				dependencies={[selectionState.isSelectionMode]}
			>
				{(node) => (
					<TreeNode
						node={node}
						isSelectionMode={selectionState.isSelectionMode}
					/>
				)}
			</Tree>
		</Virtualizer>
	)
})

interface IconSetFormProps {
	mode: 'create' | 'edit'
	uploadedId?: string
	initialName?: string
	onClose: () => void
	onSuccess?: (data: { name: string; id: string }) => void
	onDelete?: () => void
}

export function IconSetForm({
	mode,
	uploadedId,
	initialName = '',
	onClose,
	onSuccess,
	onDelete,
}: IconSetFormProps) {
	const workspaceId = useWorkspaceId()

	// Fetch icon set data for edit mode
	const { data, isLoading } = useQuery({
		...trpc.uploaded.getById.queryOptions({ id: uploadedId!, workspaceId }),
		enabled: mode === 'edit' && !!uploadedId,
	})

	// Convert fetched icons to ProcessedIcon format
	// Server returns optimized svg in icon.svg, so content is already optimized
	const fetchedIcons = useMemo<ProcessedIcon[]>(() => {
		if (!data) return []
		return data.icons.map((icon) => ({
			name: icon.name,
			path: icon.path,
			content: icon.svg,
			optimizedContent: icon.svg, // Server already returns optimized svg
		}))
	}, [data])

	const createMutation = useCreateUploaded({
		onSuccess: (data) => {
			onClose()
			onSuccess?.({ name: data.name, id: data.id })
		},
	})

	const updateMutation = useUpdateUploaded({
		onSuccess: (data) => {
			onClose()
			onSuccess?.({ name: data.name, id: data.id })
		},
	})

	const deleteMutation = useDeleteUploaded({
		onSuccess: () => {
			onClose()
			onDelete?.()
		},
	})

	const [error, setError] = useState<string | null>(null)
	const [localName, setLocalName] = useState<string | null>(null)
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

	// Processing state - localIcons is null until user modifies, then falls back to fetchedIcons
	const [processingState, setProcessingState] = useState<{
		localIcons: ProcessedIcon[] | null
		isProcessing: boolean
		error: string | null
	}>({
		localIcons: null,
		isProcessing: false,
		error: null,
	})

	// Derived values - use local state if modified, otherwise use fetched data
	const icons = processingState.localIcons ?? fetchedIcons
	const displayName = localName ?? data?.name ?? initialName

	// Check if we're currently saving/submitting the form
	const isSaving =
		mode === 'create'
			? createMutation.isPending
			: updateMutation.isPending || deleteMutation.isPending
	const isDisabled = isSaving || (mode === 'edit' && isLoading)

	// Create tree structure from icons - memoized to avoid expensive recalculation
	const tree = useMemo(() => createTreeStructure(icons), [icons])

	// Selection state
	const [selectionState, setSelectionState] = useState({
		isSelectionMode: false,
		selectedKeys: new Set<string>(),
	})

	// Handle entering/exiting selection mode
	const handleSelectionModeToggle = useCallback(() => {
		if (processingState.isProcessing || isSaving) return

		setSelectionState((prev) => {
			if (prev.isSelectionMode) {
				return { isSelectionMode: false, selectedKeys: new Set() }
			}
			return { ...prev, isSelectionMode: true }
		})
	}, [processingState.isProcessing, isSaving])

	// Handle tree selection changes - memoized for IconSetTree
	const handleTreeSelectionChange = useCallback(
		(keys: Selection) => {
			if (processingState.isProcessing || isSaving) return
			if (keys === 'all') {
				setSelectionState((prev) => ({
					...prev,
					selectedKeys: new Set(),
				}))
			} else {
				const stringKeys = new Set<string>()
				for (const key of keys) {
					stringKeys.add(String(key))
				}
				setSelectionState((prev) => ({
					...prev,
					selectedKeys: stringKeys,
				}))
			}
		},
		[processingState.isProcessing, isSaving],
	)

	// Memoize selected icon count to avoid recalculating on every render
	const selectedIconCount = useMemo(
		() => countSelectedIcons(selectionState.selectedKeys, tree),
		[selectionState.selectedKeys, tree],
	)

	// Handle deleting selected icons
	const handleDeleteSelected = useCallback(() => {
		if (selectionState.selectedKeys.size === 0) return
		const iconsToDelete = getSelectedIcons(selectionState.selectedKeys, tree)
		const remainingIcons = icons.filter(
			(icon) =>
				!iconsToDelete.some(
					(deleteIcon) =>
						deleteIcon.name === icon.name && deleteIcon.path === icon.path,
				),
		)
		setProcessingState((prev) => ({
			...prev,
			localIcons: remainingIcons,
		}))
		setSelectionState({
			isSelectionMode: false,
			selectedKeys: new Set(),
		})
	}, [selectionState.selectedKeys, tree, icons])

	// Process files and update state
	const processUploadedFiles = async (files: File[]) => {
		if (files.length === 0) return

		setProcessingState((prev) => ({
			...prev,
			isProcessing: true,
			error: null,
		}))

		try {
			const processedIcons = await processFiles(files)
			const newIcons = normalizeIconPaths(processedIcons)

			// Set icon set name based on upload type - only if field is empty and in create mode
			if (!displayName && mode === 'create') {
				let nameToSet = ''

				const zipFile = files.find((f) => f.name.endsWith('.zip'))

				if (zipFile) {
					nameToSet = zipFile.name.replace('.zip', '')
				} else {
					const directoryFile = files.find(
						(f) => f.webkitRelativePath && f.webkitRelativePath.includes('/'),
					)

					if (directoryFile) {
						const topLevelDir = directoryFile.webkitRelativePath.split('/')[0]
						if (topLevelDir) {
							nameToSet = topLevelDir
						}
					} else {
						const svgFile = files.find((f) => f.name.endsWith('.svg'))
						if (svgFile && files.length === 1) {
							nameToSet = svgFile.name.replace('.svg', '')
						}
					}
				}

				if (nameToSet) {
					setLocalName(nameToSet)
				}
			}

			// Merge with existing icons
			const allIcons = [...icons, ...newIcons]

			setProcessingState((prev) => ({
				...prev,
				localIcons: allIcons,
				isProcessing: false,
			}))
		} catch (error) {
			setProcessingState((prev) => ({
				...prev,
				error: `Failed to process files: ${error}`,
				isProcessing: false,
			}))
		}
	}

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		setError(null)

		if (!displayName.trim()) {
			setError('Icon set name is required')
			return
		}

		if (icons.length === 0) {
			setError('Please upload at least one icon')
			return
		}

		try {
			if (mode === 'create') {
				await createMutation.mutateAsync({
					name: displayName.trim(),
					icons: transformIconsForUpload(icons),
				})
			} else if (uploadedId) {
				await updateMutation.mutateAsync({
					id: uploadedId,
					name: displayName.trim(),
					icons: transformIconsForUpload(icons),
				})
			}
		} catch (err) {
			setError(getUploadErrorMessage(err))
		}
	}

	const handleDelete = async () => {
		if (!uploadedId) return
		try {
			await deleteMutation.mutateAsync({ id: uploadedId })
		} catch (err) {
			setError(getUploadErrorMessage(err))
		}
	}

	return (
		<form
			className="bg-primary flex max-h-[80vh] w-full flex-col overflow-hidden rounded-xl"
			onSubmit={handleSubmit}
		>
			{/* Header */}
			<div className="border-secondary flex items-center justify-between border-b px-6 py-4">
				<h2 className="text-primary text-lg font-semibold">
					{mode === 'create' ? 'New Icon Set' : 'Edit Icon Set'}
				</h2>
				<div className="flex items-center gap-2">
					{mode === 'edit' && !showDeleteConfirm ? (
						<>
							<Button
								color="secondary-destructive"
								size="sm"
								onClick={() => setShowDeleteConfirm(true)}
								type="button"
								isDisabled={isSaving}
								iconLeading={Trash01}
							>
								Delete
							</Button>
							<Button
								color="secondary"
								size="sm"
								onClick={onClose}
								type="button"
							>
								Cancel
							</Button>
							<Button
								type="submit"
								size="sm"
								isLoading={updateMutation.isPending}
								isDisabled={
									isDisabled ||
									processingState.isProcessing ||
									icons.length === 0
								}
							>
								Save
							</Button>
						</>
					) : mode === 'edit' && showDeleteConfirm ? (
						<>
							<Text className="text-secondary text-sm">Delete icon set?</Text>
							<Button
								color="secondary"
								size="sm"
								onClick={() => setShowDeleteConfirm(false)}
								type="button"
							>
								Cancel
							</Button>
							<Button
								color="primary-destructive"
								size="sm"
								onClick={handleDelete}
								type="button"
								isLoading={deleteMutation.isPending}
							>
								Confirm Delete
							</Button>
						</>
					) : (
						<>
							<Button
								color="secondary"
								size="sm"
								onClick={onClose}
								type="button"
							>
								Cancel
							</Button>
							<Button
								type="submit"
								size="sm"
								isLoading={createMutation.isPending}
								isDisabled={
									isSaving || processingState.isProcessing || icons.length === 0
								}
							>
								Save
							</Button>
						</>
					)}
				</div>
			</div>

			{/* Content */}
			<div className="flex flex-1 flex-col gap-4 overflow-hidden p-6">
				<Input
					label="Name"
					isRequired
					value={displayName}
					onChange={setLocalName}
					placeholder="Icon set name"
					aria-label="Icon set name"
					isDisabled={isSaving || processingState.isProcessing}
				/>

				{error && <Text className="text-xs text-red-500">{error}</Text>}

				<div className={cx('flex flex-col', icons.length > 0 ? '' : 'flex-1')}>
					<FileUpload.DropZone
						className={cx('flex-1 justify-center')}
						accept="image/svg+xml,.svg,application/zip,application/x-zip-compressed"
						allowsMultiple
						hint={
							processingState.isProcessing
								? 'Processing files...'
								: isSaving
									? 'Saving...'
									: 'Drop .svg files, directories, or .zip archives here'
						}
						isDisabled={processingState.isProcessing || isSaving}
						onDropFiles={async (files) => {
							await processUploadedFiles(Array.from(files))
						}}
						onDropUnacceptedFiles={() => {
							setProcessingState((prev) => ({
								...prev,
								error: `Unsupported file type. Only SVG files, directories with SVG files, and ZIP archives are allowed.`,
							}))
						}}
						onSizeLimitExceed={() => {
							setProcessingState((prev) => ({
								...prev,
								error: `File size too large. Please select smaller files.`,
							}))
						}}
					/>
				</div>

				{processingState.error && (
					<Text className="text-xs text-red-500">{processingState.error}</Text>
				)}

				{/* Selection bar */}
				{icons.length > 0 && (
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							{!selectionState.isSelectionMode ? (
								<Button
									color="link-color"
									size="sm"
									onClick={handleSelectionModeToggle}
									isDisabled={processingState.isProcessing || isSaving}
								>
									Select
								</Button>
							) : (
								<Button
									color="link-color"
									size="sm"
									onClick={handleSelectionModeToggle}
									isDisabled={processingState.isProcessing || isSaving}
								>
									Cancel
								</Button>
							)}
						</div>
						<div className="flex items-center gap-2">
							{selectionState.isSelectionMode &&
								selectionState.selectedKeys.size > 0 && (
									<Button
										color="secondary-destructive"
										size="sm"
										onClick={handleDeleteSelected}
										isDisabled={
											selectionState.selectedKeys.size === 0 ||
											processingState.isProcessing ||
											isSaving
										}
									>
										{`Delete ${formatCount(selectedIconCount)} icon${selectedIconCount === 1 ? '' : 's'}`}
									</Button>
								)}
						</div>
					</div>
				)}

				{/* Tree view */}
				{mode === 'edit' && isLoading ? (
					<div className="border-secondary flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-lg border py-10">
						<LoadingIndicator type="line-simple" size="lg" label="Loading..." />
					</div>
				) : icons.length > 0 ? (
					<div className="border-secondary min-h-0 flex-1 overflow-hidden rounded-lg border">
						<IconSetTree
							key={icons.length}
							tree={tree}
							selectionState={selectionState}
							onSelectionChange={handleTreeSelectionChange}
							isProcessing={processingState.isProcessing}
							isSaving={isSaving}
						/>
					</div>
				) : null}

				{/* Icon count */}
				{!(mode === 'edit' && isLoading) && icons.length > 0 && (
					<Text className="text-tertiary text-xs">
						{formatCount(icons.length)} icon
						{icons.length === 1 ? '' : 's'}
					</Text>
				)}
			</div>
		</form>
	)
}

// Tree node component - memoized to prevent unnecessary re-renders
const TreeNode = memo(function TreeNode({
	node,
}: {
	node: ReturnType<typeof createTreeStructure>[0]
	isSelectionMode: boolean
}) {
	return (
		<TreeItem key={node.id} id={node.id} textValue={node.name}>
			<TreeContent className="[&_[data-slot=icon]]:text-gray-500">
				{({ hasChildItems }) => (
					<>
						{node.isIcon && node.icon ? (
							<IconPreview icon={node.icon} />
						) : hasChildItems ? (
							<Folder className="mr-2 size-5" />
						) : (
							<FileAttachment01 className="mr-2 size-5" />
						)}
						<div className="flex-1 truncate">{node.name}</div>
						{!node.isIcon && (
							<span className="ml-2 text-xs text-gray-500">
								{formatCount(node.iconsCount)}
							</span>
						)}
					</>
				)}
			</TreeContent>
			{node.childItems.map((childNode) => (
				<TreeNode key={childNode.id} node={childNode} isSelectionMode={false} />
			))}
		</TreeItem>
	)
})

// Simple icon preview component - memoized since icon content is stable
const IconPreview = memo(function IconPreview({
	icon,
}: {
	icon: ProcessedIcon
}) {
	try {
		return (
			<div
				className="text-fg-quaternary mr-2 size-5 flex-shrink-0 *:size-full"
				dangerouslySetInnerHTML={{ __html: icon.optimizedContent }}
			/>
		)
	} catch {
		return <FileAttachment01 className="mr-2 size-5" />
	}
})
